<template>
  <li class="advance-transaction panel bg-white">
    <div class="row justify-between no-space">
      <div>
        <h4 class="transaction-title margin-0">
          {{ transactionTitle }}
          <span
            v-if="transaction.endsAt"
            class="advance-type cancelled"
          >
            {{ $gettext('Cancelled') }}
          </span>
        </h4>
        <small class="issue-date text-body-light">
          {{ $gettext('Issued') }}&nbsp;{{ formatDate(transaction.createdAt) }}
        </small>
      </div>

      <div class="text-right">
        <span class="total-text text-body-light">
          {{ $gettext('Advance') }}
        </span>
        <strong class="total-amount">
          {{ formatAmount(transaction.amount, false) }}
        </strong>
      </div>
    </div>

    <figure class="bar">
      <div
        class="bar-done"
        :style="`width: ${percentageRecouped}%;`"
      />
    </figure>

    <div class="row justify-between no-space">
      <small>
        <strong class="recouped text-secondary">
          {{ formatAmount(transaction.bundledAmount, false) }}
          {{ $gettext('recouped') }}
          {{ `(${percentageRecouped}%)` }}
        </strong>
      </small>
      <small class="remaining text-right text-body-light">
        {{ formatAmount(remainingTotal, false) }}
        {{ $gettext('remaining') }}
      </small>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useGettext } from 'vue3-gettext'
import { formatAmount } from '@/helpers/formatAmount'
import { formatDate } from '@/helpers/formatDate'
import { AdvanceType } from '@/constants'

const { $gettext } = useGettext()

const props = defineProps<{
  advanceNumber: number
  advanceType: AdvanceType
  transaction: AdvanceTransaction
}>()

const transactionTitle = computed(() => {
  let label: string
  switch (props.advanceType) {
    case AdvanceType.user:
      label = $gettext('Account Advance')
      break
    case AdvanceType.asset:
      label = $gettext('Track Advance')
      break
    default:
      label = $gettext('Release Advance')
  }

  return `${label} #${props.advanceNumber}`
})

const remainingTotal = computed(() => parseFloat(props.transaction.amount) - parseFloat(props.transaction.bundledAmount))
const percentageRecouped = computed(() => (
  remainingTotal.value === 0
    ? '100'
    : ((parseFloat(props.transaction.bundledAmount) / parseFloat(props.transaction.amount)) * 100).toFixed(2)
))
</script>

<style lang="scss" scoped>
.advance-transaction {
  margin-top: .75rem;
  padding: 20px 25px;

  @at-root .dark-theme & {
    background-color: var(--dark-brand-bg-darker) !important;
  }
}
.advance-type {
  margin-left: 1rem;
  vertical-align: bottom;
}
.transaction-title {
  font-family: var(--brand-font-body);
  font-size: 16px;
}
.issue-date {
  display: block;
  font-size: 13px;
  padding: 5px 0 9px;
}
.total-text {
  display: block;
  font-size: 11px;
  line-height: 11px;
  letter-spacing: 0.5px;
  margin: 3px 0 9px;
  text-transform: uppercase;
}
.total-amount {
  display: block;
  font-size: 20px;
}
.bar,
.bar-done {
  border-radius: 3px;
  height: 6px;
}
.bar {
  margin: 0 0 5px;
}
</style>
